import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PrintIcon from '@mui/icons-material/Print';
import type { Cell, PlayerId } from '../domain';
import {
  buildGroupRefereeForms,
  buildGroupScoreSheet,
  computeFootballStandings,
  computeStandings,
  summarizeFootballMatch,
  summarizeMatch,
} from '../domain';
import { useTournament } from '../state/TournamentContext';
import { useTranslation } from '../state/I18nContext';
import { printRefereeForms, printScoreSheets } from './printScoreSheet';
import { CellEditor } from './CellEditor';
import { groupMatchLabel } from './labels';

interface PoolMatrixProps {
  poolId: string;
}

function pairKey(a: PlayerId, b: PlayerId): string {
  return [a, b].sort().join('|');
}

export function PoolMatrix({ poolId }: PoolMatrixProps) {
  const { tournament } = useTournament();
  const { t, locale } = useTranslation();
  const group = tournament?.groups[poolId];
  const cells = useMemo<Cell[]>(() => {
    const g = tournament?.groups[poolId];
    if (!tournament || !g) return [];
    return g.cellIds.map((id) => tournament.cells[id]);
  }, [tournament, poolId]);
  const nameById = useMemo(() => {
    const g = tournament?.groups[poolId];
    const map: Record<string, string> = {};
    if (!tournament || !g) return map;
    for (const pid of g.playerIds) map[pid] = tournament.players[pid]?.name ?? pid;
    return map;
  }, [tournament, poolId]);

  const [selected, setSelected] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const cellByPair = useMemo(() => {
    const map = new Map<string, Cell>();
    for (const cell of cells) map.set(pairKey(cell.playerA, cell.playerB), cell);
    return map;
  }, [cells]);

  // Friendly "Match N" label per cell, numbered by play order so the matrix
  // tooltip, the result dialog and the printed sheet all agree.
  const matchLabelById = useMemo(() => {
    const map: Record<string, string> = {};
    if (!tournament) return map;
    for (const cell of cells) map[cell.id] = groupMatchLabel(tournament, cell, t);
    return map;
  }, [tournament, cells, t]);

  const isFootball = tournament?.sportId === 'FOOTBALL';

  const standings = useMemo(
    () => (group && !isFootball ? computeStandings(group.playerIds, cells) : []),
    [group, cells, isFootball],
  );
  // FIFA 3-1-0 table for football pools; [] otherwise so only one is computed.
  const footballStandings = useMemo(
    () =>
      group && isFootball ? computeFootballStandings(group.playerIds, cells) : [],
    [group, cells, isFootball],
  );

  if (!group) return null;

  const players = group.playerIds;
  const completed = cells.length > 0 && cells.every((c) => c.status === 'COMPLETED');

  const openCell = (cell: Cell) => {
    setSelected(cell.id);
    setOpen(true);
  };

  const handlePrint = () => {
    if (!tournament) return;
    // Football prints the referees' match-report forms (one page per match);
    // table tennis the group's fill-in score sheet.
    if (isFootball) {
      printRefereeForms(buildGroupRefereeForms(tournament, poolId), locale);
    } else {
      printScoreSheets([buildGroupScoreSheet(tournament, poolId)], locale);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Typography variant="h6">{t('groups.group', { label: group.label })}</Typography>
        <Chip
          size="small"
          label={
            isFootball
              ? t('admin.groups.chips.teams', { count: players.length })
              : t('admin.groups.chips.players', { count: players.length })
          }
          variant="outlined"
        />
        {completed ? (
          <Chip
            size="small"
            color="success"
            icon={<CheckCircleIcon />}
            label={t('groups.completed')}
          />
        ) : (
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label={t('admin.common.status.IN_PROGRESS')}
          />
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip
          title={
            isFootball
              ? t('admin.groups.printRefereeForms', { label: group.label })
              : t('admin.groups.printGroupSheet', { label: group.label })
          }
          arrow
        >
          <IconButton
            size="small"
            onClick={handlePrint}
            aria-label={
              isFootball
                ? t('admin.groups.printRefereeForms', { label: group.label })
                : t('admin.groups.printGroupSheet', { label: group.label })
            }
          >
            <PrintIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Score matrix */}
      <Box sx={{ overflowX: 'auto' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `auto repeat(${players.length}, 96px)`,
            gap: '4px',
            width: 'max-content',
          }}
        >
          <Box />
          {players.map((p) => (
            <HeaderCell key={`h-${p}`} title={`${nameById[p] ?? p} · ${p}`}>
              {nameById[p] ?? p}
            </HeaderCell>
          ))}

          {players.map((rowP) => (
            <Box key={`row-${rowP}`} sx={{ display: 'contents' }}>
              <HeaderCell title={`${nameById[rowP] ?? rowP} · ${rowP}`}>
                {nameById[rowP] ?? rowP}
              </HeaderCell>
              {players.map((colP) => {
                if (rowP === colP) return <BlockedCell key={`${rowP}-${colP}`} />;
                const cell = cellByPair.get(pairKey(rowP, colP));
                if (!cell) return <BlockedCell key={`${rowP}-${colP}`} />;
                return (
                  <ScoreCell
                    key={`${rowP}-${colP}`}
                    cell={cell}
                    rowPlayer={rowP}
                    matchLabel={matchLabelById[cell.id]}
                    football={isFootball}
                    onClick={() => openCell(cell)}
                  />
                );
              })}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Standings: FIFA 3-1-0 table for football, wins table for table tennis */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {t('admin.groups.standings')}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: isFootball
              ? '28px 1fr 28px 28px 28px 36px 36px'
              : '28px 1fr 32px',
            rowGap: '2px',
            columnGap: 1,
            alignItems: 'center',
          }}
        >
          <HeadLabel>{t('table.rank')}</HeadLabel>
          <HeadLabel>{isFootball ? t('table.team') : t('table.player')}</HeadLabel>
          {isFootball ? (
            <>
              <HeadLabel align="right">{t('table.won')}</HeadLabel>
              <HeadLabel align="right">{t('table.drawn')}</HeadLabel>
              <HeadLabel align="right">{t('table.lost')}</HeadLabel>
              <HeadLabel align="right">{t('table.goalDiff')}</HeadLabel>
              <HeadLabel align="right">{t('table.points')}</HeadLabel>
            </>
          ) : (
            <HeadLabel align="right">{t('table.wins')}</HeadLabel>
          )}
          {(isFootball ? footballStandings : standings).map((s) => {
            const qualifies = s.rank <= 2;
            return (
              <Box key={s.playerId} sx={{ display: 'contents' }}>
                <Box>
                  {qualifies ? (
                    <Chip
                      size="small"
                      label={s.rank}
                      color={s.rank === 1 ? 'primary' : 'secondary'}
                      sx={{ height: 20, '& .MuiChip-label': { px: 0.75 } }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary" align="center">
                      {s.rank}
                    </Typography>
                  )}
                </Box>
                <Tooltip
                  title={`${s.playerId} · ${nameById[s.playerId] ?? s.playerId}`}
                  arrow
                >
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{ fontWeight: qualifies ? 700 : 400, minWidth: 0 }}
                  >
                    {nameById[s.playerId] ?? s.playerId}
                  </Typography>
                </Tooltip>
                {'points' in s ? (
                  <>
                    <Num>{s.matchesWon}</Num>
                    <Num>{s.matchesDrawn}</Num>
                    <Num>{s.matchesLost}</Num>
                    <Num>{s.goalDiff > 0 ? `+${s.goalDiff}` : s.goalDiff}</Num>
                    <Num>{s.points}</Num>
                  </>
                ) : (
                  <Num>{s.matchesWon}</Num>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      <CellEditor cellId={selected} open={open} onClose={() => setOpen(false)} />
    </Paper>
  );
}

function HeaderCell({ children, title }: { children: ReactNode; title?: string }) {
  const content = (
    <Box
      sx={{
        height: 36,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 0.75,
        minWidth: 0,
      }}
    >
      <Box
        component="span"
        sx={{
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: 12,
          fontWeight: 700,
          color: 'text.secondary',
        }}
      >
        {children}
      </Box>
    </Box>
  );
  return title ? (
    <Tooltip title={title} arrow>
      {content}
    </Tooltip>
  ) : (
    content
  );
}

function BlockedCell() {
  return (
    <Box
      sx={{
        height: 36,
        borderRadius: 1,
        bgcolor: 'action.disabledBackground',
        opacity: 0.4,
      }}
    />
  );
}

function ScoreCell({
  cell,
  rowPlayer,
  matchLabel,
  football,
  onClick,
}: {
  cell: Cell;
  rowPlayer: PlayerId;
  matchLabel: string;
  football: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  const done = cell.status === 'COMPLETED';
  // A football score is live from the moment it is recorded; a best-of series
  // only shows once completed (a partial series reads as noise in the matrix).
  const live = football && cell.status === 'IN_PROGRESS';
  let mine: number;
  let theirs: number;
  if (football) {
    const summary = summarizeFootballMatch(cell.games);
    mine = cell.playerA === rowPlayer ? summary.goalsA : summary.goalsB;
    theirs = cell.playerA === rowPlayer ? summary.goalsB : summary.goalsA;
  } else {
    const summary = summarizeMatch(cell.games, cell.bestOf);
    mine = cell.playerA === rowPlayer ? summary.gamesA : summary.gamesB;
    theirs = cell.playerA === rowPlayer ? summary.gamesB : summary.gamesA;
  }
  const color = live
    ? 'warning.main'
    : !done
      ? 'text.disabled'
      : mine > theirs
        ? 'success.main'
        : mine < theirs
          ? 'error.main'
          : 'text.secondary';

  return (
    <Tooltip
      title={t('admin.groups.matchTooltip', {
        matchLabel,
        timeSlot: cell.timeSlot,
        tableIndex: cell.tableIndex,
      })}
      arrow
    >
      <ButtonBase
        onClick={onClick}
        sx={{
          height: 36,
          borderRadius: 1,
          border: 1,
          borderColor: done ? 'divider' : 'action.disabled',
          bgcolor: done ? 'action.hover' : 'transparent',
          fontWeight: 700,
          fontSize: 13,
          color,
          '&:hover': { borderColor: 'primary.main' },
        }}
      >
        {done || live ? `${mine}–${theirs}` : '·'}
      </ButtonBase>
    </Tooltip>
  );
}

function HeadLabel({
  children,
  align = 'left',
}: {
  children: ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <Typography variant="caption" color="text.secondary" align={align} sx={{ fontWeight: 700 }}>
      {children}
    </Typography>
  );
}

function Num({ children }: { children: ReactNode }) {
  return (
    <Typography variant="body2" align="right">
      {children}
    </Typography>
  );
}
